<template>
  <div class="grid-container text-base">
    <!-- Header -->
    <section-header variants="dividing" size="xl">
      <template slot="content">
        <heading level="1" size="xl" qaid="shipmentsInquiry.heading" v-html="bp('heading')" />
      </template>
      <template slot="actions">
        <div class="buttons">
          <trek-link
            button
            secondary
            small
            :href="`${encodedContextPath}/my-account/orders/`"
            qaid="shipmentsInquiry.goToOrderHistory"
          >
            <span v-html="bp('goToOrderHistory')"></span>
          </trek-link>
        </div>
      </template>
    </section-header>

    <p class="mt-3" qaid="shipmentsInquiry.description" v-html="bp('description')" />

    <section-header variants="dividing" size="md" class="mt-3">
      <template slot="content">
        <heading level="1" size="md" qaid="shipmentsInquiry.filtersHeading" v-html="bp('filter')" />
      </template>
    </section-header>

    <div class="grid-x grid-margin-x">
      <div class="cell large-4">
        <form-group name="shipDate" :label="bp('shipDate')">
          <template v-slot:input>
            <date-picker
              ref="dateRangePicker"
              mode="range"
              :allow-input="true"
              qaid="shipments-inquiry-ship-date-field"
              @change="dateFilterInput"
            />
            <span
              class="underline hover:no-underline cursor-pointer"
              qaid="shipmentsInquiry.viewLastThirtyDays"
              @click="getLast30Days"
              v-html="bp('view30Days')"
            />
          </template>
        </form-group>
        <form-group
          v-model="options.shipToNumber"
          type="text"
          name="shipToNumber"
          :label="bp('storeNumber')"
          :placeholder="bp('shipToNumberPlaceholder')"
          :maxlength="8"
          qaid="shipments-inquiry-store-number"
          @input="updateShipToValue"
        />
      </div>

      <div class="cell large-4">
        <form-group
          v-model="options.packingSlipNumber"
          type="text"
          name="customerPo"
          :label="bp('packingSlipNumber')"
          :placeholder="bp('enterSlip')"
          :maxlength="8"
          qaid="shipments-inquiry-packing-slip-number"
          @input="updatePackingSlipNumber"
        />
      </div>
      <div class="cell large-4 flex md:justify-end h-4">
        <div class="buttons buttons--right-for-md">
          <trek-button
            id="filter-shipments-button"
            primary
            class="lg:w-full"
            qaid="shipmentsInquiry.filterShipmentsButton"
            @click="fetchAllOrders"
          >
            <span v-html="bp('filter')"></span>
          </trek-button>
          <trek-button
            id="clear-filters-button"
            secondary
            class="lg:w-full"
            qaid="shipmentsInquiry.clearfilterButton"
            @click="clearFilters"
          >
            <span v-html="bp('clearFilters')"></span>
          </trek-button>
        </div>
      </div>
    </div>

    <div>
      <trek-loading page :is-loading="loading" />

      <!-- Orders Tabs -->
      <section class="my-5">
        <tabs ref="tabs" :value="currentTab" @tab-click="tabChanged">
          <tab-pane name="bike">
            <h3
              slot="label"
              class="heading heading--md m-0"
              qaid="shipmentsInquiry.bikesTab"
              v-html="`${bp('bikes')} ${bikesCount}`"
            />
            <shipments-inquiry-grid
              :grid-data="bike"
              :columns="tableHeaders"
              :base-properties="baseProperties"
              :size="bike.pageSize"
            />
          </tab-pane>
          <tab-pane name="aftermarket">
            <h3
              slot="label"
              class="heading heading--md m-0"
              qaid="shipmentsInquiry.accessoriesTab"
              v-html="`${bp('accessories')} ${aftermarketCount}`"
            />
            <shipments-inquiry-grid
              :grid-data="aftermarket"
              :columns="aftermarketHeaders"
              :base-properties="baseProperties"
              :size="aftermarket.pageSize"
            />
          </tab-pane>
        </tabs>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import {mapState} from 'vuex';
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';
import Tabs from '@/components/tabs/Tabs';
import Heading from '@/components/Heading';
import FormGroup from '@/components/FormGroup';
import TabPane from '@/components/tabs/TabPane';
import DatePicker from '@/components/DatePicker';
import TrekLoading from '@/components/TrekLoading';
import SectionHeader from '@/components/SectionHeader';
import ShipmentsInquiryGrid from '@/components/containers/my-account/ShipmentsInquiryGrid';

export default {
  components: {
    TrekButton,
    TrekLink,
    TrekLoading,
    Tabs,
    Heading,
    FormGroup,
    TabPane,
    DatePicker,
    SectionHeader,
    ShipmentsInquiryGrid,
  },
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: true,
      bike: {
        type: 'bike',
        items: null,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
      },
      aftermarket: {
        type: 'aftermarket',
        items: null,
        totalItemCount: 0,
        pageIndex: 0,
        pageSize: 50,
        pageCount: 0,
      },
      options: {},
      currentTab: 'bike',
    };
  },

  computed: {
    bikesCount() {
      return this.bike.totalItemCount ? `(${this.bike.totalItemCount})` : '(0)';
    },

    aftermarketCount() {
      return this.aftermarket.totalItemCount ? `(${this.aftermarket.totalItemCount})` : '(0)';
    },

    aftermarketHeaders() {
      // Remove the hangtag column from the aftermarket grid because they're not available for them.
      return this.tableHeaders.filter((header) => header.key !== 'hangTag');
    },

    ...mapState('backend', ['encodedContextPath']),
  },

  created() {
    this.fetchAllOrders();
  },

  provide() {
    return {
      container: this,
    };
  },

  methods: {
    async fetchAllOrders() {
      await this.fetchOrders('bike');
      this.fetchOrders('aftermarket');
    },

    fetchOrders(type = 'bike') {
      axios
        .get('/my-account/shipments/', {
          params: {
            productType: type,
            pageIndex: this[type].pageIndex,
            pageSize: this[type].pageSize,
            shipToNumber: this.options.shipToNumber,
            packingSlipNumber: this.options.packingSlipNumber,
            shippingStartDate: this.options.startDate,
            shippingEndDate: this.options.endDate,
          },
        })
        .then((response) => {
          if (response && response.data && response.data.data) {
            let data = response.data.data;

            this[type] = {
              type: type,
              items: data.shipments,
              totalItemCount: data.paging.totalItemCount,
              pageIndex: this[type].pageIndex,
              pageSize: this[type].pageSize,
            };

            if (
              this[type].totalItemCount > 0 &&
              this[type].pageSize > 0 &&
              this[type].totalItemCount / this[type].pageSize > 1
            ) {
              this[type].pageCount = Math.ceil(this[type].totalItemCount / this[type].pageSize);
            } else {
              this[type].pageCount = 0;
            }

            if (this[type].pageIndex > this[type].pageCount) {
              this[type].pageIndex = 0;
              this.fetchOrders(type);
            }

            // Forcing update on tab labels
            this.$refs.tabs.$refs.nav.$forceUpdate();

            if (this.options.packingSlipNumber) {
              if (
                Array.isArray(data.shipments) &&
                data.shipments.length > 0 &&
                data.shipments[0].productType === 'Accessories'
              ) {
                this.currentTab = 'aftermarket';
              } else {
                this.currentTab = 'bike';
              }
            }
          }

          this.loading = false;
        });
    },

    dateFilterInput({startDate, endDate}) {
      if (startDate) {
        this.options.startDate = window.flatpickr.formatDate(startDate, 'm/d/Y');
      }
      if (endDate) {
        this.options.endDate = window.flatpickr.formatDate(endDate, 'm/d/Y');
      }
    },

    tabChanged(event) {
      this.currentTab = event.paneName;
    },

    updateShipToValue() {
      const shipToMaxLength = 8;
      this.options.shipToNumber = this.options.shipToNumber.replace(/[^\d]/g, '');
      if (String(this.options.shipToNumber).length > shipToMaxLength) {
        this.options.shipToNumber = this.options.shipToNumber.substring(0, shipToMaxLength);
      }
    },

    updatePackingSlipNumber() {
      const packingSlipNumberMaxLength = 8;
      this.options.packingSlipNumber = this.options.packingSlipNumber.replace(/[^\d]/g, '');
      if (String(this.options.packingSlipNumber).length > packingSlipNumberMaxLength) {
        this.options.packingSlipNumber = this.options.packingSlipNumber.substring(0, packingSlipNumberMaxLength);
      }
    },

    getLast30Days() {
      let date = new Date();
      let endDate = date.toISOString();
      let startDate = new Date(date.setDate(date.getDate() - 30)).toISOString();

      startDate = startDate.substring(0, startDate.indexOf('T'));
      endDate = endDate.substring(0, endDate.indexOf('T'));

      this.options = {};

      this.$refs.dateRangePicker.fp.setDate([startDate, endDate]);
      this.options.startDate = window.flatpickr.formatDate(this.$refs.dateRangePicker.fp.selectedDates[0], 'm/d/Y');
      this.options.endDate = window.flatpickr.formatDate(this.$refs.dateRangePicker.fp.selectedDates[1], 'm/d/Y');

      this.fetchAllOrders();
    },

    clearFilters() {
      this.$refs.dateRangePicker.fp.clear();
      this.options = {};
      this.fetchAllOrders();
    },
  },
};
</script>
